import { useMemo, useState, type FormEvent } from 'react';
import {
  AlertTriangle, Box, Calculator, MapPin, Plus, RefreshCw, Search, X,
} from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { Dialog } from '@/components/ui/Dialog';
import { demander } from '@/components/ui/Fenetres';
import { extractError } from '@/api/client';
import { formatMontant } from '@/utils/currency';
import { formatDateFR } from '@/utils/date';
import {
  useAffecterImmobilisation, useCategoriesImmobilisation, useCreerImmobilisation,
  useDoterAmortissements, useImmobilisations, usePlanAmortissement, useSortirImmobilisation,
  useSynthesePatrimoine,
} from '@/hooks/usePatrimoine';
import type {
  CategorieImmobilisation, Immobilisation, StatutImmobilisation,
} from '@/api/patrimoine';

const LIBELLE_STATUT: Record<StatutImmobilisation, string> = {
  en_service:   'En service',
  en_reparation: 'En réparation',
  hors_service: 'Hors service',
  cede:         'Cédé',
  rebute:       'Au rebut',
};

const COULEUR_STATUT: Record<StatutImmobilisation, string> = {
  en_service:    'ds-bd-s',
  en_reparation: 'ds-bd-w',
  hors_service:  'ds-bd-d',
  cede:          'ds-bd-i',
  rebute:        'ds-bd-i',
};

/**
 * Registre du patrimoine — SIMG-PATRIMOINE-2026-001.
 *
 * Répond à la question qui n'avait pas de réponse : l'acquisition d'une
 * brouette se note où, et par qui. Un achat passé par le portail Achats
 * inscrit son bien tout seul à la réception ; cet écran sert à consulter le
 * registre, à reprendre le matériel acquis avant la plateforme, à suivre les
 * déplacements et à passer les amortissements.
 */
export const PatrimoinePage = () => {
  const [recherche, setRecherche]   = useState('');
  const [categorie, setCategorie]   = useState<CategorieImmobilisation | ''>('');
  const [statut, setStatut]         = useState<StatutImmobilisation | ''>('');
  const [dialogCreer, setDialogCreer] = useState(false);
  const [fiche, setFiche]           = useState<Immobilisation | null>(null);
  const [erreur, setErreur]         = useState<string | null>(null);
  const [message, setMessage]       = useState<string | null>(null);

  const filtres = useMemo(
    () => ({
      recherche: recherche.trim() || undefined,
      categorie: categorie || undefined,
      statut:    statut || undefined,
    }),
    [recherche, categorie, statut],
  );

  const { data: liste, isLoading, refetch } = useImmobilisations(filtres);
  const { data: synthese } = useSynthesePatrimoine();
  const { data: categories } = useCategoriesImmobilisation();
  const doter = useDoterAmortissements();

  const biens = liste?.data ?? [];

  const lancerDotation = async () => {
    const maintenant = new Date();
    const periode = `${maintenant.getFullYear()}-${String(maintenant.getMonth() + 1).padStart(2, '0')}`;
    if (!(await demander.confirmer({
      titre: `Passer les dotations d'amortissement de ${periode} pour toute la filiale ?`,
      message:
        `Une écriture OD sera créée (D 681 Dotations / C 28x Amortissements).\n` +
        `L'opération est rejouable : un bien déjà amorti ce mois-ci sera ignoré.`,
      confirmer: 'Passer les dotations',
    }))) return;

    setErreur(null);
    setMessage(null);
    try {
      const r = await doter.mutateAsync(periode);
      setMessage(
        r.nombreBiens > 0
          ? `${r.nombreBiens} bien(s) amorti(s) — ${formatMontant(r.montantTotalCents)} FCFA passés en charge.`
          : r.message,
      );
    } catch (e) {
      setErreur(extractError(e).message);
    }
  };

  return (
    <>
      <Topbar
        title="Patrimoine"
        subtitle="Registre des immobilisations — ce que le groupe possède, ce que ça vaut, où c'est"
        actions={
          <>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()} title="Rafraîchir">
              <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
            </button>
            <button
              className="ds-btn ds-btn-g"
              onClick={() => void lancerDotation()}
              disabled={doter.isPending}
              title="Passer les dotations du mois en cours"
            >
              <Calculator size={14} /> Amortir le mois
            </button>
            <button className="ds-btn ds-btn-p" onClick={() => setDialogCreer(true)}>
              <Plus size={14} /> Inscrire un bien
            </button>
          </>
        }
      />

      <div className="ds-content">
        {erreur && (
          <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico"><AlertTriangle size={18} /></span>
            <div className="ds-alert-txt">{erreur}</div>
          </div>
        )}
        {message && (
          <div className="ds-alert-strip ds-alert-s" style={{ marginBottom: 16 }}>
            <div className="ds-alert-txt">{message}</div>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => setMessage(null)}><X size={12} /></button>
          </div>
        )}

        {/* ── Ce que vaut le patrimoine ─────────────────────────────── */}
        {synthese && (
          <div className="ds-kpi-grid" style={{ marginBottom: 20 }}>
            <Tuile titre="Biens au registre" valeur={String(synthese.nombreBiens)} detail="hors cessions et rebuts" />
            <Tuile titre="Valeur d'achat"    valeur={`${formatMontant(synthese.valeurBruteCents)} F`}    detail="cumul des acquisitions" />
            <Tuile titre="Déjà amorti"       valeur={`${formatMontant(synthese.amortissementsCents)} F`} detail="dotations passées" />
            <Tuile titre="Valeur nette"      valeur={`${formatMontant(synthese.valeurNetteCents)} F`}    detail="ce qui reste à l'actif" accent />
          </div>
        )}

        {/* ── Filtres ───────────────────────────────────────────────── */}
        <div className="ds-filters">
          <div style={{ position: 'relative', flex: '1 1 240px', maxWidth: 360 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--tx-3)' }} />
            <input
              className="ds-fi"
              style={{ width: '100%', paddingLeft: 30 }}
              placeholder="Désignation, numéro d'inventaire, série, lieu…"
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
            />
          </div>
          <select className="ds-fi" style={{ width: 'auto' }} value={categorie} onChange={(e) => setCategorie(e.target.value as CategorieImmobilisation | '')}>
            <option value="">Toutes catégories</option>
            {categories?.map((c) => <option key={c.code} value={c.code}>{c.libelle}</option>)}
          </select>
          <select className="ds-fi" style={{ width: 'auto' }} value={statut} onChange={(e) => setStatut(e.target.value as StatutImmobilisation | '')}>
            <option value="">Tous les états</option>
            {(Object.keys(LIBELLE_STATUT) as StatutImmobilisation[]).map((s) => (
              <option key={s} value={s}>{LIBELLE_STATUT[s]}</option>
            ))}
          </select>
        </div>

        {/* ── Registre ──────────────────────────────────────────────── */}
        <div className="ds-table-panel">
          <div className="ds-table-header">
            <div>
              <div className="ds-table-title">Registre des immobilisations</div>
              <div className="ds-table-sub">{biens.length} bien{biens.length > 1 ? 's' : ''}</div>
            </div>
          </div>
          <table className="ds-tbl">
            <thead>
              <tr>
                <th style={{ width: 130 }}>N° inventaire</th>
                <th>Désignation</th>
                <th style={{ width: 150 }}>Catégorie</th>
                <th style={{ width: 110 }}>Acquisition</th>
                <th style={{ width: 120, textAlign: 'right' }}>Valeur d'achat</th>
                <th style={{ width: 120, textAlign: 'right' }}>Valeur nette</th>
                <th style={{ width: 150 }}>Où / qui</th>
                <th style={{ width: 100 }}>État</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32 }}>Chargement…</td></tr>}
              {!isLoading && biens.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 48 }}>
                  <Box size={28} style={{ margin: '0 auto 8px', color: 'var(--tx-3)' }} />
                  <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>Aucun bien au registre</p>
                  <p style={{ fontSize: 11, color: 'var(--tx-3)', marginTop: 4 }}>
                    Un achat de nature « immobilisation » inscrit son bien tout seul à la réception.
                  </p>
                </td></tr>
              )}
              {!isLoading && biens.map((b) => (
                <tr key={b.id} onClick={() => setFiche(b)} style={{ cursor: 'pointer' }}>
                  <td><span className="ds-ref-code">{b.codeInventaire}</span></td>
                  <td>
                    <div className="ds-entry-label">{b.designation}</div>
                    {(b.marque || b.numeroSerie) && (
                      <div style={{ fontSize: 10, color: 'var(--tx-3)' }}>
                        {[b.marque, b.modele, b.numeroSerie && `n° ${b.numeroSerie}`].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className="ds-bd ds-bd-i" style={{ fontSize: 10 }}>
                      {categories?.find((c) => c.code === b.categorie)?.libelle ?? b.categorie}
                    </span>
                    <div style={{ fontSize: 10, color: 'var(--tx-3)', marginTop: 2 }}>
                      compte {b.compteImmobilisationCode}
                    </div>
                  </td>
                  <td style={{ fontSize: 11 }}>{formatDateFR(b.dateAcquisition)}</td>
                  <td style={{ textAlign: 'right', fontSize: 12 }}>{formatMontant(b.valeurAcquisitionCents)}</td>
                  <td style={{ textAlign: 'right', fontSize: 12, fontWeight: 600 }}>
                    {formatMontant(b.valeurNetteCents)}
                  </td>
                  <td style={{ fontSize: 11 }}>
                    {b.localisation ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <MapPin size={11} style={{ color: 'var(--tx-3)' }} /> {b.localisation}
                      </div>
                    ) : <span style={{ color: 'var(--tx-3)' }}>non affecté</span>}
                    {b.detenteurNom && <div style={{ color: 'var(--tx-3)' }}>{b.detenteurNom}</div>}
                  </td>
                  <td>
                    <span className={`ds-bd ${COULEUR_STATUT[b.statut]}`} style={{ fontSize: 10 }}>
                      {LIBELLE_STATUT[b.statut]}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <DialogCreerBien open={dialogCreer} onClose={() => setDialogCreer(false)} />
      <FicheBien bien={fiche} onClose={() => setFiche(null)} />
    </>
  );
};

// ── Tuile de synthèse ────────────────────────────────────────────────────

const Tuile = ({ titre, valeur, detail, accent }: { titre: string; valeur: string; detail: string; accent?: boolean }) => (
  <div className="ds-kpi-card">
    <div className="ds-kpi-label">{titre}</div>
    <div className="ds-kpi-value" style={accent ? { color: 'var(--primary)' } : undefined}>{valeur}</div>
    <div className="ds-kpi-detail">{detail}</div>
  </div>
);

// ── Inscription d'un bien ────────────────────────────────────────────────

const DialogCreerBien = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const { data: categories } = useCategoriesImmobilisation();
  const creer = useCreerImmobilisation();

  const [origine, setOrigine]         = useState<'reprise' | 'achat_hors_bca'>('achat_hors_bca');
  const [reglement, setReglement]     = useState<'fournisseur' | 'banque' | 'caisse'>('fournisseur');
  const [designation, setDesignation] = useState('');
  const [categorie, setCategorie]     = useState<CategorieImmobilisation>('materiel_outillage');
  const [valeur, setValeur]           = useState('');
  const [dateAcq, setDateAcq]         = useState(new Date().toISOString().slice(0, 10));
  const [duree, setDuree]             = useState('');
  const [cumul, setCumul]             = useState('');
  const [marque, setMarque]           = useState('');
  const [numeroSerie, setSerie]       = useState('');
  const [localisation, setLieu]       = useState('');
  const [detenteur, setDetenteur]     = useState('');
  const [erreur, setErreur]           = useState<string | null>(null);

  const reglesCategorie = categories?.find((c) => c.code === categorie);

  const soumettre = async (e: FormEvent) => {
    e.preventDefault();
    setErreur(null);
    const francs = Number(valeur);
    if (!designation.trim() || !Number.isFinite(francs) || francs <= 0) {
      setErreur('Désignation et valeur d\'achat sont obligatoires.');
      return;
    }
    try {
      await creer.mutateAsync({
        origine,
        reglement:               origine === 'achat_hors_bca' ? reglement : undefined,
        designation:             designation.trim(),
        categorie,
        // L'utilisateur saisit des francs, la plateforme stocke des centimes.
        valeurAcquisitionCents:  Math.round(francs * 100),
        dateAcquisition:         dateAcq,
        dureeAmortissementMois:  duree ? Number(duree) : undefined,
        cumulAmortissementCents: origine === 'reprise' && cumul ? Math.round(Number(cumul) * 100) : undefined,
        marque:                  marque.trim() || undefined,
        numeroSerie:             numeroSerie.trim() || undefined,
        localisation:            localisation.trim() || undefined,
        detenteurNom:            detenteur.trim() || undefined,
      });
      setDesignation(''); setValeur(''); setDuree(''); setCumul('');
      setMarque(''); setSerie(''); setLieu(''); setDetenteur('');
      onClose();
    } catch (e2) {
      setErreur(extractError(e2).message);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title="Inscrire un bien au registre">
      <form onSubmit={soumettre}>
        <p style={{ fontSize: 11, color: 'var(--tx-3)', marginBottom: 14 }}>
          Pour le matériel acquis hors de la plateforme, ou détenu avant elle.
          Un achat passé par le portail Achats inscrit son bien tout seul à la réception.
        </p>

        <Champ label="Origine du bien">
          <select className="ds-fi" style={{ width: '100%' }} value={origine}
                  onChange={(e) => setOrigine(e.target.value as 'reprise' | 'achat_hors_bca')}>
            <option value="achat_hors_bca">Acheté en 2026 sans bon de commande — l'écriture est passée tout de suite</option>
            <option value="reprise">Détenu avant 2026 — entre en comptabilité par la balance d'ouverture</option>
          </select>
        </Champ>
        {origine === 'achat_hors_bca' && (
          <div style={{ marginTop: 12 }}>
            <Champ label="Réglé">
              <select className="ds-fi" style={{ width: '100%' }} value={reglement}
                      onChange={(e) => setReglement(e.target.value as 'fournisseur' | 'banque' | 'caisse')}>
                <option value="fournisseur">À crédit — dû au fournisseur (compte 481000)</option>
                <option value="banque">Par banque (compte 521000)</option>
                <option value="caisse">En espèces (compte 571000)</option>
              </select>
            </Champ>
          </div>
        )}
        <div style={{ height: 12 }} />

        {erreur && (
          <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 12 }}>
            <div className="ds-alert-txt">{erreur}</div>
          </div>
        )}

        <div style={{ display: 'grid', gap: 12 }}>
          <Champ label="Désignation">
            <input className="ds-fi" style={{ width: '100%' }} value={designation}
                   onChange={(e) => setDesignation(e.target.value)}
                   placeholder="Brouette de chantier renforcée 100 L" autoFocus />
          </Champ>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Champ label="Catégorie">
              <select className="ds-fi" style={{ width: '100%' }} value={categorie}
                      onChange={(e) => setCategorie(e.target.value as CategorieImmobilisation)}>
                {categories?.map((c) => <option key={c.code} value={c.code}>{c.libelle}</option>)}
              </select>
            </Champ>
            <Champ label="Valeur d'achat (FCFA)">
              <input className="ds-fi" style={{ width: '100%' }} type="number" min={1} value={valeur}
                     onChange={(e) => setValeur(e.target.value)} placeholder="120000" />
            </Champ>
          </div>

          {reglesCategorie && (
            <p style={{ fontSize: 11, color: 'var(--tx-3)', marginTop: -4 }}>
              Compte {reglesCategorie.compteImmobilisation} · amorti sur {reglesCategorie.dureeAnsParDefaut} ans
              par défaut, amortissement au compte {reglesCategorie.compteAmortissement}.
            </p>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Champ label="Date d'acquisition">
              <input className="ds-fi" style={{ width: '100%' }} type="date" value={dateAcq}
                     onChange={(e) => setDateAcq(e.target.value)} />
            </Champ>
            <Champ label="Durée (mois)">
              <input className="ds-fi" style={{ width: '100%' }} type="number" min={0} value={duree}
                     onChange={(e) => setDuree(e.target.value)}
                     placeholder={reglesCategorie ? String(reglesCategorie.dureeMoisParDefaut) : ''} />
            </Champ>
          </div>

          {origine === 'reprise' && <Champ label="Amortissements déjà pratiqués (FCFA)">
            <input className="ds-fi" style={{ width: '100%' }} type="number" min={0} value={cumul}
                   onChange={(e) => setCumul(e.target.value)} placeholder="0" />
            <p style={{ fontSize: 10, color: 'var(--tx-3)', marginTop: 4 }}>
              À renseigner pour un bien repris en cours de vie : sans cela, l'actif serait surévalué dès la première clôture.
            </p>
          </Champ>}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Champ label="Marque et modèle">
              <input className="ds-fi" style={{ width: '100%' }} value={marque}
                     onChange={(e) => setMarque(e.target.value)} placeholder="Mercedes Actros" />
            </Champ>
            <Champ label="Numéro de série">
              <input className="ds-fi" style={{ width: '100%' }} value={numeroSerie}
                     onChange={(e) => setSerie(e.target.value)} placeholder="Visible sur le bien" />
            </Champ>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Champ label="Où se trouve le bien">
              <input className="ds-fi" style={{ width: '100%' }} value={localisation}
                     onChange={(e) => setLieu(e.target.value)} placeholder="Centrale de Bouaké" />
            </Champ>
            <Champ label="Qui en répond">
              <input className="ds-fi" style={{ width: '100%' }} value={detenteur}
                     onChange={(e) => setDetenteur(e.target.value)} placeholder="Chef de parc" />
            </Champ>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={creer.isPending}>
            {creer.isPending ? 'Enregistrement…' : 'Inscrire au registre'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

const Champ = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--tx-2)', marginBottom: 4 }}>
      {label}
    </label>
    {children}
  </div>
);

// ── Fiche d'un bien ──────────────────────────────────────────────────────

const FicheBien = ({ bien, onClose }: { bien: Immobilisation | null; onClose: () => void }) => {
  const { data: plan } = usePlanAmortissement(bien?.id ?? null);
  const affecter = useAffecterImmobilisation();
  const sortir   = useSortirImmobilisation();
  const [lieu, setLieu]         = useState('');
  const [detenteur, setDet]     = useState('');
  const [erreur, setErreur]     = useState<string | null>(null);

  if (!bien) return null;

  const deplacer = async () => {
    if (!lieu.trim() && !detenteur.trim()) return;
    setErreur(null);
    try {
      await affecter.mutateAsync({
        id:           bien.id,
        localisation: lieu.trim() || undefined,
        detenteurNom: detenteur.trim() || undefined,
        motif:        'Affectation depuis le registre',
      });
      setLieu(''); setDet('');
      onClose();
    } catch (e) { setErreur(extractError(e).message); }
  };

  const mettreAuRebut = async () => {
    const motif = await demander.texte({ titre: 'Sortie du bien', libelle: 'Motif de la sortie (perte, destruction, vol, vente…)', confirmer: 'Sortir le bien', danger: true });
    if (!motif?.trim()) return;
    setErreur(null);
    try {
      await sortir.mutateAsync({ id: bien.id, type: 'rebut', motif: motif.trim() });
      onClose();
    } catch (e) { setErreur(extractError(e).message); }
  };

  const restant = plan?.filter((e) => !e.comptabilise).length ?? 0;

  return (
    <Dialog open={!!bien} onClose={onClose} title={`${bien.codeInventaire} — ${bien.designation}`}>
      {erreur && (
        <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 12 }}>
          <div className="ds-alert-txt">{erreur}</div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 6, fontSize: 12, marginBottom: 16 }}>
        <Info label="Valeur d'achat"  valeur={`${formatMontant(bien.valeurAcquisitionCents)} FCFA`} />
        <Info label="Déjà amorti"     valeur={`${formatMontant(bien.cumulAmortissementCents)} FCFA`} />
        <Info label="Valeur nette"    valeur={`${formatMontant(bien.valeurNetteCents)} FCFA`} fort />
        <Info label="Acquis le"       valeur={formatDateFR(bien.dateAcquisition)} />
        <Info label="Durée"           valeur={`${bien.dureeAmortissementMois} mois`} />
        <Info label="Comptes"         valeur={`${bien.compteImmobilisationCode} / ${bien.compteAmortissementCode}`} />
        <Info label="État"            valeur={LIBELLE_STATUT[bien.statut]} />
        <Info label="Localisation"    valeur={bien.localisation ?? 'non affecté'} />
        {bien.detenteurNom && <Info label="Détenteur" valeur={bien.detenteurNom} />}
        {bien.fournisseur && <Info label="Fournisseur" valeur={bien.fournisseur.raisonSociale} />}
      </div>

      {plan && plan.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx-2)', marginBottom: 6 }}>
            Amortissement — {plan.length - restant} échéance(s) passée(s), {restant} à venir
          </div>
          <div style={{ height: 8, background: 'var(--bg-3)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              width: `${Math.round(((plan.length - restant) / plan.length) * 100)}%`,
              background: 'var(--primary)',
            }} />
          </div>
        </div>
      )}

      {bien.statut !== 'cede' && bien.statut !== 'rebute' && (
        <div style={{ borderTop: '1px solid var(--bd)', paddingTop: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx-2)', marginBottom: 8 }}>
            Déplacer ce bien
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8 }}>
            <input className="ds-fi" placeholder="Nouveau lieu" value={lieu} onChange={(e) => setLieu(e.target.value)} />
            <input className="ds-fi" placeholder="Nouveau détenteur" value={detenteur} onChange={(e) => setDet(e.target.value)} />
            <button className="ds-btn ds-btn-p" onClick={() => void deplacer()} disabled={affecter.isPending}>
              Affecter
            </button>
          </div>
          <button
            className="ds-btn ds-btn-g ds-btn-sm"
            style={{ marginTop: 10, color: 'var(--danger)' }}
            onClick={() => void mettreAuRebut()}
            disabled={sortir.isPending}
          >
            Sortir de l'actif
          </button>
        </div>
      )}

      {bien.dateSortie && (
        <div style={{ borderTop: '1px solid var(--bd)', paddingTop: 12, fontSize: 11, color: 'var(--tx-3)' }}>
          Sorti le {formatDateFR(bien.dateSortie)} — {bien.motifSortie}
        </div>
      )}
    </Dialog>
  );
};

const Info = ({ label, valeur, fort }: { label: string; valeur: string; fort?: boolean }) => (
  <>
    <span style={{ color: 'var(--tx-3)' }}>{label}</span>
    <span style={fort ? { fontWeight: 700 } : undefined}>{valeur}</span>
  </>
);
